import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaCode, FaWordpress, FaSearch, FaServer, FaGithub, FaLinkedin, FaFacebook, FaTwitter, FaMapMarkerAlt, FaEnvelope, FaPhone, FaDownload, FaArrowRight } from 'react-icons/fa';
import API from '../api/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutPage = () => {
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    Promise.allSettled([API.get('/profile'), API.get('/skills'), API.get('/experience')])
      .then(([p, s, e]) => {
        if (p.status === 'fulfilled') setProfile(p.value.data);
        if (s.status === 'fulfilled') setSkills(s.value.data);
        if (e.status === 'fulfilled') setExperiences(e.value.data);
      });
  }, []);

  const highlights = [
    { icon: <FaCode />, label: 'MERN Stack', desc: 'MongoDB, Express, React, Node.js', color: '#61DAFB' },
    { icon: <FaWordpress />, label: 'WordPress', desc: 'Elementor, WooCommerce, Themes', color: '#21759B' },
    { icon: <FaSearch />, label: 'SEO Expert', desc: 'On-Page, Off-Page, Technical SEO', color: '#4caf50' },
    { icon: <FaServer />, label: 'Full Stack', desc: 'Frontend + Backend + Database', color: '#ff6584' }
  ];

  const checklist = [
    'Custom Web Application Development', 'Responsive & Mobile-First Design',
    'E-Commerce Solutions (WooCommerce)', 'Search Engine Optimization (SEO)',
    'REST API Design & Integration', 'Website Speed & Performance Optimization',
    'WordPress Theme & Plugin Development', 'Database Design & Management',
    'Git Version Control', 'Deployment & Hosting Setup'
  ];

  const defaultExp = [
    { _id: '1', position: 'Full Stack Developer', company: 'Freelance', startDate: '2022', endDate: 'Present', description: 'Building custom web applications, WordPress sites, and providing SEO services for clients worldwide.' },
    { _id: '2', position: 'Frontend Developer', company: 'Web Agency', startDate: '2021', endDate: '2022', description: 'Developed responsive websites using React.js, HTML/CSS, and Bootstrap for various clients.' },
  ];

  const displayExp = experiences.length > 0 ? experiences : defaultExp;

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '80px' }}>
        {/* Hero Banner */}
        <section style={{ background: 'var(--gradient-hero)', padding: '80px 0 60px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(108,99,255,0.15) 0%, transparent 70%)', top: '-100px', right: '-100px', borderRadius: '50%' }} />
          <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '50px', flexWrap: 'wrap' }}>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
              style={{ width: '200px', height: '200px', borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%', background: 'var(--gradient-1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5rem', fontWeight: '800', color: 'white', flexShrink: 0 }}>
              {(profile?.name || 'A').charAt(0)}
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} style={{ flex: 1, minWidth: '280px' }}>
              <h1 style={{ fontSize: '2.5rem', marginBottom: '5px' }}>{profile?.name || 'Asif'}</h1>
              <p style={{ fontSize: '1.2rem', color: 'var(--accent)', marginBottom: '15px' }}>{profile?.title || 'Full Stack Web Developer'}</p>
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                {profile?.bio || "Passionate Full Stack Developer with expertise in MERN Stack, WordPress, and SEO. I build modern, scalable web solutions that help businesses grow online."}
              </p>
              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                {profile?.location && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaMapMarkerAlt style={{ color: 'var(--primary)' }} /> {profile.location}</span>}
                {profile?.email && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaEnvelope style={{ color: 'var(--primary)' }} /> {profile.email}</span>}
                {profile?.phone && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaPhone style={{ color: 'var(--primary)' }} /> {profile.phone}</span>}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <Link to="/resume" className="btn btn-primary btn-small"><FaDownload /> Resume</Link>
                <Link to="/services" className="btn btn-outline btn-small"><FaArrowRight /> Services</Link>
                {profile?.social?.github && <a href={profile.social.github} target="_blank" rel="noreferrer" className="btn btn-glass btn-small"><FaGithub /></a>}
                {profile?.social?.linkedin && <a href={profile.social.linkedin} target="_blank" rel="noreferrer" className="btn btn-glass btn-small"><FaLinkedin /></a>}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Expertise Cards */}
        <section className="section" style={{ background: 'var(--bg-dark)' }}>
          <div className="container">
            <div className="section-title"><h2>My Expertise</h2><p>Core areas I specialize in</p></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {highlights.map((h, i) => (
                <motion.div key={i} className="admin-card" style={{ textAlign: 'center', cursor: 'default' }}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -5, borderColor: h.color }}>
                  <div style={{ fontSize: '2.5rem', color: h.color, marginBottom: '15px' }}>{h.icon}</div>
                  <h3 style={{ marginBottom: '8px' }}>{h.label}</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{h.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What I Do */}
        <section className="section" style={{ background: 'var(--bg-card)' }}>
          <div className="container">
            <div className="section-title"><h2>What I Can Do</h2><p>Services and capabilities</p></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', maxWidth: '900px', margin: '0 auto' }}>
              {checklist.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 18px', background: 'var(--bg-dark)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                  <FaCheckCircle style={{ color: 'var(--accent)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience Timeline */}
        <section className="section" style={{ background: 'var(--bg-dark)' }}>
          <div className="container">
            <div className="section-title"><h2>Experience</h2><p>My professional journey</p></div>
            <div className="timeline">
              {displayExp.map((exp, idx) => (
                <motion.div key={exp._id} className="timeline-item"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.15 }}>
                  <div className="timeline-content">
                    <h3>{exp.position}</h3><h4>{exp.company}</h4>
                    <p className="date">{exp.startDate} - {exp.endDate || 'Present'}</p>
                    <p>{exp.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section style={{ background: 'var(--gradient-1)', padding: '60px 0' }}>
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px', textAlign: 'center' }}>
              {[{ val: '3+', label: 'Years Experience' }, { val: '50+', label: 'Projects Done' }, { val: '30+', label: 'Happy Clients' }, { val: '100%', label: 'Satisfaction' }].map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: 'white' }}>{s.val}</h2>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer profile={profile} />
    </>
  );
};

export default AboutPage;
